import { assertNever } from "@/packages/ts-utils";
import type {
  ActiveDeploymentRegion,
  DeploymentRegion,
} from "@/utils/deployment";
import { getDeploymentRegion } from "@/utils/o11y/environment";

export const POSTHOG_INGEST_HOST = {
  us: "https://us.i.posthog.com",
  eu: "https://eu.i.posthog.com",
} as const satisfies Record<ActiveDeploymentRegion, string>;

export const POSTHOG_ASSET_HOST = {
  us: "us-assets.i.posthog.com",
  eu: "eu-assets.i.posthog.com",
} as const satisfies Record<ActiveDeploymentRegion, string>;

export const POSTHOG_UI_HOST = {
  us: "https://us.posthog.com",
  eu: "https://eu.posthog.com",
} as const satisfies Record<ActiveDeploymentRegion, string>;

/** `missing` follows the US stack. Hosts are not configurable per deploy. */
export const captureRegion = (
  region: DeploymentRegion
): ActiveDeploymentRegion => {
  switch (region) {
    case "us":
    case "missing":
      return "us";
    case "eu":
      return "eu";
    default:
      return assertNever(region);
  }
};

export const getPostHogUiHost = (): string =>
  POSTHOG_UI_HOST[captureRegion(getDeploymentRegion())];

export const postHogReplayUrl = (sessionRecordingId: string): string =>
  `${getPostHogUiHost()}/replay/${sessionRecordingId}`;

const ingestHostname = (region: ActiveDeploymentRegion): string =>
  new URL(POSTHOG_INGEST_HOST[region]).hostname;

// Carries product and marketing traffic to this stack's region; the token in each request picks the project.
export const POSTHOG_PROXY_PREFIX = "/ph";

export type PostHogProxyTarget = {
  hostname: string;
  prefix: string;
  region: ActiveDeploymentRegion;
  kind: "asset" | "ingest";
};

export const postHogProxyTarget = ({
  pathname,
  region,
}: {
  pathname: string;
  region: DeploymentRegion;
}): PostHogProxyTarget | null => {
  const isProxied =
    pathname === POSTHOG_PROXY_PREFIX ||
    pathname.startsWith(`${POSTHOG_PROXY_PREFIX}/`);
  if (!isProxied) return null;
  const capture = captureRegion(region);
  const upstreamPath = pathname.slice(POSTHOG_PROXY_PREFIX.length);
  const isAsset =
    upstreamPath.startsWith("/static/") || upstreamPath.startsWith("/array/");
  return {
    hostname: isAsset ? POSTHOG_ASSET_HOST[capture] : ingestHostname(capture),
    prefix: POSTHOG_PROXY_PREFIX,
    region: capture,
    kind: isAsset ? "asset" : "ingest",
  };
};
